// src/components/common/SupportModal.js
import React, { useState } from 'react';
import { X, Send, Loader2, MessageSquare, RotateCcw, HelpCircle } from 'lucide-react';
import { db, auth } from '../../firebase/config';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore'; 

const SupportModal = ({ isOpen, onClose, currentUserProfile }) => {
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [sendCopy, setSendCopy] = useState(true); 

    if (!isOpen) return null;

    // --- RESTART TOUR LOGIC ---
    const handleRestartTour = () => {
        if (window.confirm("Restart the interactive walkthrough?")) {
            localStorage.setItem('spatchy_restart_tour', 'true');
            // FORCE REDIRECT TO HOME so the tour actually starts
            window.location.href = '/'; 
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        setIsSending(true);

        try {
            const userEmail = currentUserProfile?.email || 'Unknown';
            const userName = currentUserProfile?.name || 'Unknown User';
            const orgId = currentUserProfile?.organizationId || 'unknown';
            
            let orgName = currentUserProfile?.organizationName || 'Unknown Org';
            
            if (orgName === 'Unknown Org' && orgId !== 'unknown') {
                try {
                    const orgRef = doc(db, 'organizations', orgId);
                    const orgSnap = await getDoc(orgRef);
                    if (orgSnap.exists() && orgSnap.data().name) {
                        orgName = orgSnap.data().name;
                    }
                } catch (fetchErr) {
                    console.log("Could not fetch org name:", fetchErr);
                }
            }

            const emailHtml = `
                <h2>🆘 New Support Request</h2>
                <p><strong>User:</strong> ${userName} (<a href="mailto:${userEmail}">${userEmail}</a>)</p>
                <p><strong>Organization:</strong> ${orgName} (ID: ${orgId})</p>
                <hr />
                <h3>Issue Description:</h3>
                <p style="font-size: 16px; white-space: pre-wrap;">${message}</p>
                <hr />
                <h3>Debug Context:</h3>
                <ul>
                    <li><strong>URL:</strong> ${window.location.href}</li>
                    <li><strong>Screen:</strong> ${window.innerWidth}x${window.innerHeight}</li>
                    <li><strong>Browser:</strong> ${navigator.userAgent}</li>
                    <li><strong>Role:</strong> ${currentUserProfile?.role || 'N/A'}</li>
                </ul>
            `;

            const ticketData = {
                to: ['marcus@spatchy.co'], 
                // x5rules-8d3f2a61 — X5 (#15): the copy goes to the SIGN-IN email only (firestore.rules
                // supportTicketCcIsSelf); the profile email is self-editable, so the rules no longer accept it.
                cc: sendCopy && auth.currentUser?.email ? [auth.currentUser.email] : [],
                message: {
                    subject: `[Spatchy Support] Help Needed: ${orgName}`,
                    html: emailHtml,
                },
                userId: currentUserProfile?.id || 'unknown',
                orgId: orgId,
                orgName: orgName,
                status: 'new',
                createdAt: serverTimestamp(),
                plainTextDescription: message
            };

            await addDoc(collection(db, 'support_tickets'), ticketData);

            setSent(true);
            setTimeout(() => {
                setSent(false);
                setMessage('');
                onClose();
            }, 2500);
        } catch (error) {
            console.error("Failed to send support request:", error);
            alert("Failed to send. Please email support directly.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                
                {/* HEADER */}
                <div className="bg-gray-900 p-4 flex justify-between items-center text-white">
                    <h3 className="font-bold flex items-center gap-2 text-lg">
                        <MessageSquare size={20} className="text-blue-400" /> Help & Support
                    </h3>
                    <button onClick={onClose} className="hover:bg-gray-700 p-1.5 rounded-full transition-colors"><X size={20} /></button>
                </div>

                <div className="p-6">
                    {sent ? (
                        <div className="text-center py-8 text-green-600">
                            <div className="mb-2 text-5xl">✅</div>
                            <h4 className="font-bold text-lg">Message Sent!</h4>
                            <p className="text-sm text-gray-500">We've received your ticket.</p>
                        </div>
                    ) : (
                        <>
                            <form onSubmit={handleSubmit} className="mb-6">
                                <p className="text-sm text-gray-600 mb-4 font-medium">
                                    Describe your issue below. Our team will respond shortly.
                                </p>

                                <textarea
                                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px] outline-none transition-shadow"
                                    placeholder="I was trying to create an invoice when..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    autoFocus
                                    required
                                />

                                <div className="mt-3 flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="sendCopy"
                                        checked={sendCopy}
                                        onChange={(e) => setSendCopy(e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="sendCopy" className="text-xs text-gray-500 cursor-pointer select-none">
                                        Send me a copy of this request
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 mt-4">
                                    <button 
                                        type="submit" 
                                        disabled={isSending || !message.trim()}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm active:scale-[0.98]"
                                    >
                                        {isSending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                                        {isSending ? 'Sending...' : 'Send Request'}
                                    </button>
                                </div>
                            </form>

                            {/* --- QUICK ACTIONS DIVIDER --- */}
                            <div className="relative flex py-2 items-center">
                                <div className="flex-grow border-t border-gray-200"></div>
                                <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Quick Actions</span>
                                <div className="flex-grow border-t border-gray-200"></div>
                            </div>

                            {/* --- RESTART TOUR BUTTON --- */}
                            <button 
                                onClick={handleRestartTour}
                                type="button"
                                className="w-full mt-2 flex items-center justify-center gap-3 px-4 py-3 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 border border-gray-200 transition-colors group"
                            >
                                <div className="p-1.5 bg-white rounded border border-gray-200 group-hover:border-blue-200 group-hover:text-blue-600 transition-colors">
                                    <RotateCcw size={16} />
                                </div>
                                <div className="text-left">
                                    <span className="block text-gray-900 font-semibold text-xs">Restart Walkthrough</span>
                                    <span className="block text-gray-500 text-[10px]">Show the feature tour again</span>
                                </div>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportModal;